import { useEffect, useState } from 'react';
import { Plus, Trash, PencilSimple, Check, ListChecks, CaretDown, UsersThree, X, Package, Copy, Eye, DotsSixVertical, ClockCounterClockwise } from '@phosphor-icons/react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Badge } from '../ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { toast } from 'sonner';
import { errText } from '../../utils/errors';
import { catLabels, catColors, STAFF_ROLES, sampleStatusColors, storageLabelsStudy } from './studyConstants';
import ConfirmDeleteDialog from '../ConfirmDeleteDialog';
import SampleDetailDialog from '../SampleDetailDialog';
import { useAuth } from '../../contexts/AuthContext';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { CustomTableDesigner } from '../CustomTable';
import { CompositeScaleDesigner } from '../CompositeScale';
import { QuestionnairePreviewDialog } from './QuestionnairePreviewDialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const DEFAULT_SATISFACTION_PREAMBLE = 'Tras el uso del producto durante el periodo indicado, valore su grado de acuerdo con las siguientes afirmaciones marcando una de las 5 opciones (1 = Totalmente en desacuerdo, 5 = Totalmente de acuerdo).';

// URS-F316: filas draggable del editor de cuestionarios.
// Usamos el indice como id estable durante la operacion de drag (la libreria
// solo necesita ids unicos durante el ciclo mouse-down -> mouse-up, y la array
// se actualiza solo en onDragEnd via arrayMove).
function SortableQuestionRow({ id, children }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
  };
  return (
    <div ref={setNodeRef} style={style} data-testid={`sq-row-${id}`}>
      {children({ attributes, listeners, isDragging })}
    </div>
  );
}

// URS-F487: popover con historial ALCOA+ de un cuestionario (creacion +
// modificaciones cronologicas con fecha, tipo e iniciales del usuario).
function QuestionnaireHistoryPopover({ studyId, questionnaireId, lang = 'es' }) {
  const [open, setOpen] = useState(false);
  const [history, setHistory] = useState(null); // null = no cargado
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(
        `${API_URL}/api/studies/${studyId}/questionnaires/${questionnaireId}/history`,
        { withCredentials: true },
      );
      setHistory(r.data || []);
    } catch {
      setHistory([]);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (open && history === null) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const fmt = (iso) => {
    if (!iso) return '-';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yy = d.getFullYear();
    const hh = String(d.getHours()).padStart(2, '0');
    const mi = String(d.getMinutes()).padStart(2, '0');
    return lang === 'es' ? `${dd}/${mm}/${yy} ${hh}:${mi}` : `${yy}-${mm}-${dd} ${hh}:${mi}`;
  };

  const labelForAction = (a) => {
    if (a === 'CREATE') return lang === 'es' ? 'Creación' : 'Created';
    if (a === 'UPDATE') return lang === 'es' ? 'Modificación' : 'Modified';
    if (a === 'DELETE') return lang === 'es' ? 'Eliminación' : 'Deleted';
    return a;
  };
  const dotClass = (a) => {
    if (a === 'CREATE') return 'bg-emerald-500';
    if (a === 'UPDATE') return 'bg-[#1f3e80]';
    if (a === 'DELETE') return 'bg-red-500';
    return 'bg-stone-400';
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7 text-stone-500 hover:text-[#1f3e80]"
          data-testid={`history-questionnaire-${questionnaireId}`}
          title={lang === 'es' ? 'Historial de cambios' : 'Change history'}
          onClick={e => e.stopPropagation()}
        >
          <ClockCounterClockwise size={14} />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="w-80 p-0 text-xs"
        data-testid={`history-popover-${questionnaireId}`}
        onClick={e => e.stopPropagation()}
      >
        <div className="px-3 py-2 border-b border-stone-100 bg-stone-50 flex items-center justify-between">
          <span className="text-[11px] font-semibold uppercase tracking-wider text-stone-500">
            {lang === 'es' ? 'Historial ALCOA+' : 'ALCOA+ history'}
          </span>
          {history && history.length > 0 && (
            <span className="text-[10px] text-stone-400 font-mono">
              {history.length} {lang === 'es' ? 'evento' + (history.length === 1 ? '' : 's') : 'event' + (history.length === 1 ? '' : 's')}
            </span>
          )}
        </div>
        <div className="max-h-72 overflow-y-auto">
          {loading ? (
            <div className="px-3 py-4 text-stone-400 text-center">{lang === 'es' ? 'Cargando…' : 'Loading…'}</div>
          ) : !history || history.length === 0 ? (
            <div className="px-3 py-4 text-stone-400 text-center">
              {lang === 'es' ? 'Sin eventos registrados' : 'No history'}
            </div>
          ) : (
            <ul className="divide-y divide-stone-100">
              {history.map((h, idx) => (
                <li key={idx} className="px-3 py-2 flex items-start gap-2" data-testid={`history-entry-${idx}`}>
                  <span className={`inline-block h-2 w-2 mt-1.5 rounded-full flex-shrink-0 ${dotClass(h.action)}`} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-medium text-stone-800">{labelForAction(h.action)}</span>
                      <span
                        className="inline-flex items-center justify-center h-5 min-w-[26px] rounded-full bg-stone-100 border border-stone-200 text-[10px] font-mono font-semibold text-stone-700"
                        title={h.user_name || h.user_email}
                      >
                        {h.user_initials}
                      </span>
                    </div>
                    <div className="text-[11px] text-stone-500 font-mono">{fmt(h.timestamp)}</div>
                    {h.user_name && (
                      <div className="text-[11px] text-stone-500 truncate" title={h.user_email}>{h.user_name}</div>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}



export function QuestionnairesPanel({ studyId, lang }) {
  const { user } = useAuth();
  const isAdmin = (user?.roles || (user?.role ? [user.role] : [])).includes('admin');
  const [questionnaires, setQuestionnaires] = useState([]);
  const [bankQuestions, setBankQuestions] = useState([]);
  const [configQuestionnaires, setConfigQuestionnaires] = useState([]);
  const [showDialog, setShowDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', type: 'general', questions: [], preamble_es: '' });
  const [showAddQ, setShowAddQ] = useState(false);
  const [showCustomQ, setShowCustomQ] = useState(false);
  const [showImportQ, setShowImportQ] = useState(false);
  const [filterCat, setFilterCat] = useState('all');
  const [confirmDeleteSQ, setConfirmDeleteSQ] = useState(null);
  // URS-F353: vista previa del cuestionario antes de asignarlo a una visita.
  const [previewQ, setPreviewQ] = useState(null);
  const [customQ, setCustomQ] = useState({ text_es: '', text_en: '', type: 'text', options: [], category: 'general', cell_options: [], show_observations: true, condition_qid: '', condition_equals: 'si' });
  const [customOpts, setCustomOpts] = useState('');
  // URS-F301: indice de la pregunta especifica que se esta editando (en form.questions).
  // null = modo "anadir nueva". >=0 = modo "guardar cambios sobre la existente".
  const [editingQuestionIdx, setEditingQuestionIdx] = useState(null);
  const [saving, setSaving] = useState(false);
  const [expandedQ, setExpandedQ] = useState({});

  const lb = lang === 'es'
    ? { new: 'Nuevo Cuestionario', edit: 'Editar Cuestionario', name: 'Nombre', type: 'Tipo', questions: 'Preguntas', save: 'Guardar', cancel: 'Cancelar', no_q: 'No hay cuestionarios', confirm_delete: 'Eliminar este cuestionario?', add_from_bank: 'Banco de preguntas', add_custom: 'Pregunta especifica', import_config: 'Importar cuestionario', no_questions: 'Sin preguntas', question_text: 'Texto pregunta', all: 'Todas', select_questions: 'Seleccionar preguntas del banco', custom_question: 'Pregunta especifica del estudio', import_title: 'Importar cuestionario de configuracion', no_config: 'No hay cuestionarios en configuracion' }
    : { new: 'New Questionnaire', edit: 'Edit Questionnaire', name: 'Name', type: 'Type', questions: 'Questions', save: 'Save', cancel: 'Cancel', no_q: 'No questionnaires', confirm_delete: 'Delete this questionnaire?', add_from_bank: 'Question bank', add_custom: 'Custom question', import_config: 'Import questionnaire', no_questions: 'No questions', question_text: 'Question text', all: 'All', select_questions: 'Select from question bank', custom_question: 'Study-specific question', import_title: 'Import questionnaire from config', no_config: 'No questionnaires in settings' };

  const qTypes = [
    { value: 'general', es: 'General', en: 'General' },
    { value: 'tolerancia', es: 'Tolerancia', en: 'Tolerance' },
    { value: 'eficacia', es: 'Eficacia', en: 'Efficacy' },
    { value: 'seguridad', es: 'Seguridad', en: 'Safety' },
    { value: 'dermatologia', es: 'Dermatologia', en: 'Dermatology' },
    { value: 'satisfaccion', es: 'Satisfaccion', en: 'Satisfaction' },
    // URS-F501: tipo `eventos_adversos` habilita en el rellenado
    // check "No aplica" + sub-pestanas EA1/EA2 (multi-entry).
    { value: 'eventos_adversos', es: 'Eventos Adversos', en: 'Adverse Events' },
    // URS-F503: tipo `medicacion_concomitante` — mismas features
    // multi-entry (Med1/Med2/...) + "No aplica (Sin cambios)".
    { value: 'medicacion_concomitante', es: 'Medicación Concomitante', en: 'Concomitant Medication' },
  ];

  const load = () => {
    if (!studyId) return;
    Promise.all([
      axios.get(`${API_URL}/api/studies/${studyId}/questionnaires`, { withCredentials: true }),
      axios.get(`${API_URL}/api/config/questions`, { withCredentials: true }),
      axios.get(`${API_URL}/api/config/questionnaires`, { withCredentials: true }),
    ]).then(([qr, br, cr]) => {
      setQuestionnaires(qr.data);
      setBankQuestions(br.data.filter(q => q.active !== false));
      setConfigQuestionnaires(cr.data);
    }).catch(() => {});
  };
  useEffect(() => { load(); }, [studyId]);

  const openCreate = () => { setEditing(null); setForm({ name: '', type: 'general', questions: [], preamble_es: '' }); setShowAddQ(false); setShowCustomQ(false); setShowImportQ(false); setEditingQuestionIdx(null); setShowDialog(true); };
  const openEdit = (q) => { setEditing(q); setForm({ name: q.name, type: q.type, questions: q.questions || [], preamble_es: q.preamble_es || '' }); setShowAddQ(false); setShowCustomQ(false); setShowImportQ(false); setEditingQuestionIdx(null); setShowDialog(true); };

  // URS-F301: cargar una pregunta especifica existente en el editor inline para
  // modificarla sin tener que borrarla y volver a crearla.
  const openEditQuestion = (idx) => {
    const q = form.questions[idx];
    if (!q) return;
    if (q.source !== 'custom') {
      toast.error(lang === 'es' ? 'Solo se pueden editar preguntas específicas. Las del banco se editan en Configuración.' : 'Only custom questions are editable here. Bank questions are edited in Settings.');
      return;
    }
    // Reverse-map likert: si la pregunta es likert con scale_size, el tipo del
    // editor debe ser likert_1_5 o likert_1_7 (no el tipo interno 'likert').
    let editorType = q.type;
    if (q.type === 'likert' && q.scale_size === 7) editorType = 'likert_1_7';
    else if ((q.type === 'likert_1_5' || (q.type === 'likert' && q.scale_size === 5))) editorType = 'likert_1_5';
    setCustomQ({
      text_es: q.text_es || '',
      text_en: q.text_en || '',
      type: editorType,
      options: q.options || [],
      category: q.category || 'general',
      cell_options: q.cell_options || [],
      show_observations: q.show_observations !== false,
      // URS-F352: recargar la estructura de tabla personalizada al editar.
      table_rows: q.table_rows || 2,
      table_cols: q.table_cols || 2,
      table_corner: q.table_corner || '',
      table_headers_row: q.table_headers_row || [],
      table_headers_col: q.table_headers_col || [],
      table_cell_types: q.table_cell_types || [],
      // URS-F358: recargar composite_scale.
      composite_items: q.composite_items || [],
      composite_total_label: q.composite_total_label || 'Total',
      // Logica condicional (mostrar solo si otra pregunta Si/No cumple).
      condition_qid: q.condition?.question_id || '',
      condition_equals: q.condition?.equals || 'si',
    });
    // Para tipos con opciones (incluidas filas de tabla), prerellenamos el input csv
    const isOptType = ['scale', 'select', 'multiselect', 'radio', 'table_text', 'table_select', 'nps_multi', 'checklist'].includes(editorType);
    setCustomOpts(isOptType ? (q.options || []).join(', ') : '');
    setEditingQuestionIdx(idx);
    setShowCustomQ(true);
    setShowAddQ(false);
    setShowImportQ(false);
  };

  const cancelCustomQuestion = () => {
    setCustomQ({ text_es: '', text_en: '', type: 'text', options: [], category: 'general', cell_options: [], show_observations: true, condition_qid: '', condition_equals: 'si' });
    setCustomOpts('');
    setEditingQuestionIdx(null);
    setShowCustomQ(false);
  };

  // URS-F302: titulos de seccion para agrupar visualmente preguntas en el
  // cuestionario (mismo concepto que SatisfactionQuestionnairesPanel).
  // VolunteersPanel.js los renderiza como divisores al rellenar el cuestionario.
  const addSectionHeader = () => {
    setForm(p => ({
      ...p,
      questions: [...p.questions, { type: 'section_header', title_es: '', source: 'custom', order: p.questions.length }],
    }));
  };
  const updateSectionTitle = (idx, val) => setForm(p => ({
    ...p,
    questions: p.questions.map((q, i) => i === idx ? { ...q, title_es: val } : q),
  }));

  const handleImportConfig = (cq) => {
    const imported = (cq.questions || []).map(q => ({
      question_id: q.question_id, text_es: q.text_es, text_en: q.text_en || '', type: q.type,
      options: q.options || [], scale_id: q.scale_id || null,
      // URS-F244: propagar campos especificos de tipos tabla y derivados
      cell_type: q.cell_type || 'text',
      cell_options: q.cell_options || [],
      show_observations: q.show_observations !== false,
      source: 'bank',
    }));
    const existingIds = new Set(form.questions.map(q => q.question_id));
    const newQs = imported.filter(q => !existingIds.has(q.question_id));
    if (newQs.length === 0) { toast.info(lang === 'es' ? 'Todas las preguntas ya estan anadidas' : 'All questions already added'); return; }
    // URS-F501: al importar desde config, SIEMPRE propagar `type` y
    // `name` (si esta vacio) para que el cuestionario de estudio herede
    // el comportamiento del template (ej. `eventos_adversos` habilita
    // el check "No aplica" y las sub-pestanas EA).
    setForm(p => ({
      ...p,
      questions: [...p.questions, ...newQs],
      type: cq.type || p.type,
      name: p.name.trim() ? p.name : cq.name,
    }));
    setShowImportQ(false);
    toast.success(`${newQs.length} ${lang === 'es' ? 'preguntas importadas' : 'questions imported'}`);
  };

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error(lang === 'es' ? 'El nombre es obligatorio' : 'Name is required'); return; }
    // URS-F302: validar que ningun titulo de seccion este vacio antes de guardar.
    const emptySection = (form.questions || []).find(q => q.type === 'section_header' && !(q.title_es || '').trim());
    if (emptySection) { toast.error(lang === 'es' ? 'Hay un título de sección vacío' : 'There is an empty section title'); return; }
    setSaving(true);
    try {
      if (editing) { await axios.put(`${API_URL}/api/studies/${studyId}/questionnaires/${editing.questionnaire_id}`, form, { withCredentials: true }); toast.success(lang === 'es' ? 'Cuestionario actualizado' : 'Questionnaire updated'); }
      else { await axios.post(`${API_URL}/api/studies/${studyId}/questionnaires`, form, { withCredentials: true }); toast.success(lang === 'es' ? 'Cuestionario creado' : 'Questionnaire created'); }
      setShowDialog(false); load();
    } catch (err) { toast.error(errText(err)); }
    setSaving(false);
  };

  const handleDelete = async (qId) => {
    try { await axios.delete(`${API_URL}/api/studies/${studyId}/questionnaires/${qId}`, { withCredentials: true }); toast.success(lang === 'es' ? 'Cuestionario eliminado' : 'Questionnaire deleted'); setConfirmDeleteSQ(null); load(); }
    catch (err) { toast.error(errText(err)); }
  };

  // URS-F287: duplicar cuestionario dentro del estudio
  const handleDuplicateQuestionnaire = async (q) => {
    try {
      const res = await axios.post(
        `${API_URL}/api/studies/${studyId}/questionnaires/${q.questionnaire_id}/duplicate`,
        {},
        { withCredentials: true },
      );
      toast.success(lang === 'es' ? `Cuestionario duplicado: ${res.data.name}` : `Questionnaire duplicated: ${res.data.name}`);
      load();
    } catch (err) {
      toast.error(errText(err));
    }
  };

  const addBankQuestion = (bq) => {
    if (form.questions.some(q => q.question_id === bq.question_id)) { toast.error(lang === 'es' ? 'Pregunta ya agregada' : 'Question already added'); return; }
    setForm(p => ({ ...p, questions: [...p.questions, {
      question_id: bq.question_id, text_es: bq.text_es, text_en: bq.text_en, type: bq.type,
      options: bq.options, scale_id: bq.scale_id || null,
      // URS-F244: propagar campos de tipos tabla / observaciones
      cell_type: bq.cell_type || 'text',
      cell_options: bq.cell_options || [],
      show_observations: bq.show_observations !== false,
      // URS-F270/F271: la categoria es necesaria para que el render decida si
      // mostrar la 3a columna "Relacion con el producto" (solo cuando
      // category === 'tolerancia') y para futuros filtros por categoria.
      category: bq.category || null,
      // URS-F262/F265: propagar tamanyo de la escala Likert si aplica.
      scale_size: bq.scale_size || null,
      source: 'bank', order: p.questions.length,
    }] }));
  };

  const addCustomQuestion = () => {
    if (!customQ.text_es.trim()) {
      toast.error(lang === 'es' ? 'Falta el enunciado de la pregunta (texto en español)' : 'The question wording is required (Spanish text)');
      // Auto-focus para dirigir al usuario al campo correcto. URS-F352b.
      const el = document.querySelector('[data-testid="custom-question-text"]');
      if (el && typeof el.focus === 'function') {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        setTimeout(() => el.focus(), 200);
      }
      return;
    }
    const opts = customOpts ? customOpts.split(',').map(s => s.trim()).filter(Boolean) : [];
    // URS-F264: tipos Likert llevan escala fija y options pre-rellenados.
    let qType = customQ.type;
    let qOpts = opts;
    let scaleSize = null;
    if (qType === 'likert_1_5' || qType === 'likert_1_7') {
      scaleSize = qType === 'likert_1_7' ? 7 : 5;
      // Internamente unificamos al tipo 'likert' (con scale_size) que ya
      // renderiza VolunteersPanel + PublicSurvey. Conservamos likert_1_5 como
      // tipo cuando scale_size=5 por retrocompatibilidad de filtros existentes.
      qType = scaleSize === 7 ? 'likert' : 'likert_1_5';
      qOpts = Array.from({ length: scaleSize }, (_, i) => String(i + 1));
    }
    // URS-F301: en modo edicion conservamos el question_id original para no
    // huerfanar las respuestas ya recogidas; en modo nueva generamos uno nuevo.
    const isEdit = editingQuestionIdx !== null;
    const existing = isEdit ? form.questions[editingQuestionIdx] : null;
    const newQ = {
      question_id: isEdit && existing ? existing.question_id : `custom-${Date.now()}`,
      text_es: customQ.text_es,
      text_en: customQ.text_en,
      type: qType,
      options: qOpts,
      // URS-F296: categoria define si la pregunta es elegible como Evento Adverso.
      // 'seguridad' + boolean_with_obs -> EA si si.
      // 'tolerancia' + table_select -> EA si alguna fila > 0.
      category: customQ.category || 'general',
      source: 'custom',
      order: isEdit && existing ? (existing.order ?? editingQuestionIdx) : form.questions.length,
    };
    if (scaleSize) newQ.scale_size = scaleSize;
    // URS-F297: campos extra para tipos tabla (mismo shape que en el banco global).
    if (qType === 'table_text' || qType === 'table_select') {
      newQ.show_observations = customQ.show_observations !== false;
      newQ.cell_type = qType === 'table_text' ? 'text' : 'select';
      if (qType === 'table_select') {
        newQ.cell_options = (customQ.cell_options || []).filter(Boolean);
      }
    }
    // URS-F352: custom_table — copia los campos diseñados del editor.
    if (qType === 'custom_table') {
      newQ.show_observations = customQ.show_observations !== false;
      newQ.table_rows = customQ.table_rows || 2;
      newQ.table_cols = customQ.table_cols || 2;
      newQ.table_corner = customQ.table_corner || '';
      newQ.table_headers_row = customQ.table_headers_row || [];
      newQ.table_headers_col = customQ.table_headers_col || [];
      newQ.table_cell_types = customQ.table_cell_types || [];
    }
    // URS-F358: composite_scale — copia sub-apartados y etiqueta del total.
    if (qType === 'composite_scale') {
      newQ.composite_items = (customQ.composite_items || []).filter(it => it.label && it.scale_id);
      newQ.composite_total_label = customQ.composite_total_label || 'Total';
    }
    // Logica condicional: mostrar esta pregunta solo si una Si/No anterior cumple.
    if (customQ.condition_qid && customQ.condition_qid !== '__none') {
      newQ.condition = { question_id: customQ.condition_qid, equals: customQ.condition_equals === 'no' ? 'no' : 'si' };
    }
    if (isEdit) {
      setForm(p => ({ ...p, questions: p.questions.map((q, i) => i === editingQuestionIdx ? newQ : q) }));
      toast.success(lang === 'es' ? 'Pregunta actualizada' : 'Question updated');
    } else {
      setForm(p => ({ ...p, questions: [...p.questions, newQ] }));
    }
    setCustomQ({ text_es: '', text_en: '', type: 'text', options: [], category: 'general', cell_options: [], show_observations: true, condition_qid: '', condition_equals: 'si' });
    setCustomOpts('');
    setEditingQuestionIdx(null);
    setShowCustomQ(false);
  };

  const removeQuestion = (idx) => { setForm(p => ({ ...p, questions: p.questions.filter((_, i) => i !== idx) })); };
  const filteredBank = filterCat === 'all' ? bankQuestions : bankQuestions.filter(q => q.category === filterCat);
  const selectedIds = new Set(form.questions.map(q => q.question_id));

  // URS-F316: drag&drop para reordenar preguntas y secciones en el editor.
  const dndSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
  const handleQuestionsDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIdx = parseInt(active.id, 10);
    const newIdx = parseInt(over.id, 10);
    if (Number.isNaN(oldIdx) || Number.isNaN(newIdx)) return;
    setForm(p => ({ ...p, questions: arrayMove(p.questions, oldIdx, newIdx) }));
  };

  if (!studyId) return <p className="text-sm text-stone-500 text-center py-8">{lang === 'es' ? 'Guarda el estudio primero para gestionar cuestionarios' : 'Save the study first to manage questionnaires'}</p>;

  return (
    <div className="space-y-4">
      <div className="flex justify-end gap-2">
        <SatisfactionImportButton studyId={studyId} lang={lang} onImported={load} />
        <Button data-testid="create-questionnaire-btn" onClick={openCreate} className="bg-[#1f3e80] hover:bg-[#2a4f8a] text-white rounded-sm gap-2"><Plus size={16} />{lb.new}</Button>
      </div>
      {questionnaires.length === 0 ? (
        <div className="bg-white border border-stone-200 rounded-sm p-12 text-center"><ListChecks size={40} className="text-stone-300 mx-auto mb-3" /><p className="text-sm text-stone-500">{lb.no_q}</p></div>
      ) : (
        <div className="space-y-3">
          {questionnaires.map(q => (
            <div key={q.questionnaire_id} data-testid={`questionnaire-card-${q.questionnaire_id}`} className="bg-white border border-stone-200 rounded-sm">
              <div className="flex items-center justify-between p-4 cursor-pointer select-none" data-testid={`questionnaire-toggle-${q.questionnaire_id}`} onClick={() => setExpandedQ(prev => ({ ...prev, [q.questionnaire_id]: !prev[q.questionnaire_id] }))}>
                <div className="flex items-center gap-3">
                  <CaretDown size={14} className={`text-stone-400 transition-transform duration-200 ${expandedQ[q.questionnaire_id] ? 'rotate-0' : '-rotate-90'}`} />
                  <h3 className="text-sm font-semibold text-stone-900">{q.name}</h3>
                  <Badge variant="outline" className={`text-xs ${catColors[q.type] || catColors.general}`}>{catLabels[q.type] || q.type}</Badge>
                  <Badge variant="outline" className="text-xs font-mono">{(q.questions || []).filter(qq => qq.type !== 'section_header').length} {lang === 'es' ? 'preguntas' : 'questions'}</Badge>
                  {(q.questions || []).some(qq => qq.type === 'section_header') && (
                    <Badge variant="outline" className="text-[10px] bg-violet-50 text-violet-700 border-violet-200">{(q.questions || []).filter(qq => qq.type === 'section_header').length} {lang === 'es' ? 'secciones' : 'sections'}</Badge>
                  )}
                </div>
                <div className="flex gap-1" onClick={e => e.stopPropagation()}>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 text-stone-500 hover:text-[#1f3e80]"
                    data-testid={`preview-questionnaire-${q.questionnaire_id}`}
                    onClick={() => setPreviewQ(q)}
                    title={lang === 'es' ? 'Vista previa' : 'Preview'}
                  ><Eye size={14} /></Button>
                  <QuestionnaireHistoryPopover studyId={studyId} questionnaireId={q.questionnaire_id} lang={lang} />
                  <Button variant="ghost" size="icon" className="h-7 w-7" data-testid={`edit-questionnaire-${q.questionnaire_id}`} onClick={() => openEdit(q)}><PencilSimple size={14} /></Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 text-stone-500 hover:text-[#1f3e80]"
                    data-testid={`duplicate-questionnaire-${q.questionnaire_id}`}
                    onClick={() => handleDuplicateQuestionnaire(q)}
                    title={lang === 'es' ? 'Duplicar cuestionario' : 'Duplicate questionnaire'}
                  ><Copy size={14} /></Button>
                  {isAdmin && <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500" data-testid={`delete-questionnaire-${q.questionnaire_id}`} onClick={() => setConfirmDeleteSQ(q)}><Trash size={14} /></Button>}
                </div>
              </div>
              {expandedQ[q.questionnaire_id] && (q.questions || []).length > 0 && (
                <div className="px-4 pb-4 border-t border-stone-100 pt-3">
                  {q.type === 'inclusion_exclusion' ? (() => {
                    const inclusions = q.questions.filter(qq => qq.source === 'inclusion');
                    const exclusions = q.questions.filter(qq => qq.source === 'exclusion');
                    const obs = q.questions.filter(qq => qq.source === 'observations');
                    let num = 0;
                    return (<>
                      {inclusions.length > 0 && (<>
                        <div className="flex items-center gap-2 mb-1 mt-1"><span className="text-[10px] font-semibold uppercase tracking-wider text-green-700 bg-green-50 border border-green-200 rounded px-2 py-0.5">{lang === 'es' ? 'Criterios de Inclusion' : 'Inclusion Criteria'}</span><div className="flex-1 border-t border-green-200" /></div>
                        {inclusions.map(qq => { num++; return (<div key={qq.question_id} className="flex items-center gap-2 text-xs text-stone-600 pl-2 py-0.5"><span className="font-mono text-stone-400">{num}.</span><span>{qq.text_es}</span></div>); })}
                      </>)}
                      {exclusions.length > 0 && (<>
                        <div className="flex items-center gap-2 mb-1 mt-2"><span className="text-[10px] font-semibold uppercase tracking-wider text-red-700 bg-red-50 border border-red-200 rounded px-2 py-0.5">{lang === 'es' ? 'Criterios de Exclusion' : 'Exclusion Criteria'}</span><div className="flex-1 border-t border-red-200" /></div>
                        {exclusions.map(qq => { num++; return (<div key={qq.question_id} className="flex items-center gap-2 text-xs text-stone-600 pl-2 py-0.5"><span className="font-mono text-stone-400">{num}.</span><span>{qq.text_es}</span></div>); })}
                      </>)}
                      {obs.map(qq => { num++; return (<div key={qq.question_id} className="flex items-center gap-2 text-xs text-stone-500 pl-2 py-0.5 mt-2 border-t border-stone-100 pt-2"><span className="font-mono text-stone-400">{num}.</span><span className="italic">{qq.text_es}</span></div>); })}
                    </>);
                  })() : (
                    <div className="space-y-1">
                      {(() => {
                        // URS-F315: numeracion correlativa SALTANDO los titulos de
                        // seccion; los headers se muestran como divisores.
                        let qNum = 0;
                        return q.questions.map((qq, i) => {
                          if (qq.type === 'section_header') {
                            return (
                              <div key={i} className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-wider text-violet-700 bg-violet-50 border border-violet-200 rounded-sm px-2 py-1 mt-2">
                                <Badge variant="outline" className="text-[10px] bg-violet-100 text-violet-700 border-violet-300">{lang === 'es' ? 'Sección' : 'Section'}</Badge>
                                <span>{qq.title_es || ''}</span>
                              </div>
                            );
                          }
                          qNum += 1;
                          return (
                            <div key={i} className="flex items-center gap-2 text-xs text-stone-600 pl-2">
                              <span className="font-mono text-stone-400">{qNum}.</span><span>{qq.text_es}</span>
                              {qq.source === 'custom' && <Badge variant="outline" className="text-[10px] bg-violet-50 text-violet-600 border-violet-200">{lang === 'es' ? 'Especifica' : 'Custom'}</Badge>}
                            </div>
                          );
                        });
                      })()}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader><DialogTitle>{editing ? lb.edit : lb.new}</DialogTitle></DialogHeader>
          <div className="space-y-4 overflow-y-auto flex-1 pr-1">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5"><Label>{lb.name} <span className="text-red-500">*</span></Label><Input data-testid="questionnaire-name-input" value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} /></div>
              <div className="space-y-1.5"><Label>{lb.type}</Label>
                <Select value={form.type} onValueChange={v => setForm(p => ({
                  ...p,
                  type: v,
                  // URS-F267: al cambiar a 'satisfaccion' auto-rellena el preambulo
                  // por defecto si estaba vacio; al salir del tipo limpia el preambulo
                  // solo si era el default (asi se respeta cualquier edicion manual).
                  preamble_es: v === 'satisfaccion'
                    ? (p.preamble_es && p.preamble_es.trim() ? p.preamble_es : DEFAULT_SATISFACTION_PREAMBLE)
                    : (p.preamble_es === DEFAULT_SATISFACTION_PREAMBLE ? '' : p.preamble_es),
                }))}>
                  <SelectTrigger data-testid="questionnaire-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{qTypes.map(t => (<SelectItem key={t.value} value={t.value}>{lang === 'es' ? t.es : t.en}</SelectItem>))}</SelectContent>
                </Select>
              </div>
            </div>
            {form.type === 'satisfaccion' && (
              <div className="space-y-1.5" data-testid="study-q-preamble-block">
                <div className="flex items-center justify-between">
                  <Label>{lang === 'es' ? 'Preámbulo (mostrado al voluntario)' : 'Preamble (shown to volunteer)'}</Label>
                  <Button
                    variant="ghost"
                    size="sm"
                    type="button"
                    className="text-[11px] h-6 px-2"
                    data-testid="study-q-preamble-reset"
                    onClick={() => setForm(p => ({ ...p, preamble_es: DEFAULT_SATISFACTION_PREAMBLE }))}
                  >
                    {lang === 'es' ? 'Restaurar por defecto' : 'Reset default'}
                  </Button>
                </div>
                <Textarea
                  data-testid="study-q-preamble-input"
                  value={form.preamble_es || ''}
                  onChange={e => setForm(p => ({ ...p, preamble_es: e.target.value }))}
                  className="min-h-[80px] text-sm"
                  placeholder={lang === 'es' ? 'Texto de introducción que verá el voluntario...' : 'Intro text shown to the volunteer...'}
                />
                <p className="text-[10px] text-stone-400">{lang === 'es' ? 'Texto editable solo para este cuestionario. Por defecto usa la plantilla común de satisfacción.' : 'Editable text for this questionnaire only. Defaults to the common satisfaction template.'}</p>
              </div>
            )}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-sm font-medium">{lb.questions} ({form.questions.filter(q => q.type !== 'section_header').length})</Label>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" data-testid="import-config-q-btn" onClick={() => setShowImportQ(!showImportQ)} className="text-xs gap-1 rounded-sm"><Plus size={12} />{lb.import_config}</Button>
                  <Button variant="outline" size="sm" data-testid="add-bank-question-btn" onClick={() => setShowAddQ(!showAddQ)} className="text-xs gap-1 rounded-sm"><Plus size={12} />{lb.add_from_bank}</Button>
                  <Button variant="outline" size="sm" data-testid="add-custom-question-btn" onClick={() => { if (showCustomQ) { cancelCustomQuestion(); } else { setShowCustomQ(true); setEditingQuestionIdx(null); } }} className="text-xs gap-1 rounded-sm"><Plus size={12} />{lb.add_custom}</Button>
                  <Button variant="outline" size="sm" data-testid="add-section-header-btn" onClick={addSectionHeader} className="text-xs gap-1 rounded-sm border-violet-300 text-violet-700 hover:bg-violet-50" title={lang === 'es' ? 'Insertar un título para agrupar las preguntas que vengan debajo' : 'Insert a section title to group following questions'}><Plus size={12} />{lang === 'es' ? 'Título de sección' : 'Section title'}</Button>
                </div>
              </div>
              {form.questions.length === 0 ? (<p className="text-xs text-stone-400 text-center py-4">{lb.no_questions}</p>) : (
                <DndContext sensors={dndSensors} collisionDetection={closestCenter} onDragEnd={handleQuestionsDragEnd}>
                  <SortableContext items={form.questions.map((_, i) => String(i))} strategy={verticalListSortingStrategy}>
                    <div className="space-y-1 mb-3">{(() => {
                  // URS-F315: numeracion correlativa SALTANDO los titulos de
                  // seccion. Los headers no son preguntas y no deben llevar
                  // numero ni ocupar posicion en el conteo de respuestas.
                  let qNum = 0;
                  return form.questions.map((q, i) => {
                  if (q.type === 'section_header') {
                    return (
                    <SortableQuestionRow key={i} id={String(i)}>
                      {({ attributes, listeners }) => (
                    <div className="flex items-center gap-2 bg-violet-50 border border-violet-200 rounded-sm px-2 py-2" data-testid={`section-header-${i}`}>
                      <button
                        type="button"
                        {...attributes}
                        {...listeners}
                        className="cursor-grab active:cursor-grabbing text-violet-400 hover:text-violet-700 p-0.5 shrink-0"
                        aria-label={lang === 'es' ? 'Arrastrar para reordenar' : 'Drag to reorder'}
                        title={lang === 'es' ? 'Arrastrar para reordenar' : 'Drag to reorder'}
                        data-testid={`sq-drag-handle-${i}`}
                      ><DotsSixVertical size={14} weight="bold" /></button>
                      <Badge variant="outline" className="text-[10px] bg-violet-100 text-violet-700 border-violet-300">{lang === 'es' ? 'Sección' : 'Section'}</Badge>
                      <Input
                        value={q.title_es || ''}
                        onChange={e => updateSectionTitle(i, e.target.value)}
                        placeholder={lang === 'es' ? 'Título de la sección...' : 'Section title...'}
                        className="text-sm font-semibold flex-1 bg-white border-violet-200"
                        data-testid={`section-header-input-${i}`}
                      />
                      <Button variant="ghost" size="icon" className="h-6 w-6 text-stone-400 hover:text-red-500" onClick={() => removeQuestion(i)} title={lang === 'es' ? 'Eliminar' : 'Remove'}><Trash size={12} /></Button>
                    </div>
                      )}
                    </SortableQuestionRow>
                    );
                  }
                  qNum += 1;
                  const displayNum = qNum;
                  return (
                  <SortableQuestionRow key={i} id={String(i)}>
                    {({ attributes, listeners }) => (
                  <div className={`flex items-center justify-between border rounded-sm px-2 py-2 ${editingQuestionIdx === i ? 'bg-violet-50 border-violet-300' : 'bg-stone-50 border-stone-200'}`}>
                    <div className="flex items-center gap-2 min-w-0">
                      <button
                        type="button"
                        {...attributes}
                        {...listeners}
                        className="cursor-grab active:cursor-grabbing text-stone-400 hover:text-stone-700 p-0.5 shrink-0"
                        aria-label={lang === 'es' ? 'Arrastrar para reordenar' : 'Drag to reorder'}
                        title={lang === 'es' ? 'Arrastrar para reordenar' : 'Drag to reorder'}
                        data-testid={`sq-drag-handle-${i}`}
                      ><DotsSixVertical size={14} weight="bold" /></button>
                      <span className="font-mono text-xs text-stone-400 shrink-0">{displayNum}.</span>
                      <span className="text-sm text-stone-800 truncate">{q.text_es}</span>
                      {q.source === 'custom' && <Badge variant="outline" className="text-[10px] bg-violet-50 text-violet-600 border-violet-200 shrink-0">{lang === 'es' ? 'Especifica' : 'Custom'}</Badge>}
                      {q.source === 'bank' && <Badge variant="outline" className="text-[10px] bg-blue-50 text-blue-600 border-blue-200 shrink-0">{lang === 'es' ? 'Banco' : 'Bank'}</Badge>}
                    </div>
                    <div className="flex items-center gap-0.5 shrink-0">
                      {q.source === 'custom' && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6 text-stone-400 hover:text-violet-600"
                          data-testid={`edit-custom-question-${i}`}
                          onClick={() => openEditQuestion(i)}
                          title={lang === 'es' ? 'Editar pregunta' : 'Edit question'}
                        ><PencilSimple size={12} /></Button>
                      )}
                      <Button variant="ghost" size="icon" className="h-6 w-6 text-stone-400 hover:text-red-500" onClick={() => removeQuestion(i)} title={lang === 'es' ? 'Eliminar' : 'Remove'}><Trash size={12} /></Button>
                    </div>
                  </div>
                    )}
                  </SortableQuestionRow>
                  );
                });
                })()}</div>
                  </SortableContext>
                </DndContext>
              )}
              {showImportQ && (
                <div className="border border-purple-200 bg-purple-50/30 rounded-sm p-3 mb-3">
                  <p className="text-xs font-semibold text-stone-700 mb-2">{lb.import_title}</p>
                  {configQuestionnaires.length === 0 ? (<p className="text-xs text-stone-400 text-center py-2">{lb.no_config}</p>) : (
                    <div className="max-h-[200px] overflow-y-auto space-y-1">{configQuestionnaires.map(cq => (
                      <div key={cq.questionnaire_id} className="flex items-center justify-between bg-white border border-stone-200 rounded-sm px-3 py-2">
                        <div className="flex items-center gap-2"><span className="text-sm font-medium text-stone-800">{cq.name}</span><Badge variant="outline" className="text-[10px]">{(cq.questions || []).length} {lang === 'es' ? 'preg.' : 'q.'}</Badge></div>
                        <Button variant="outline" size="sm" className="text-xs gap-1 h-7 rounded-sm" data-testid={`import-cq-${cq.questionnaire_id}`} onClick={() => handleImportConfig(cq)}>{lang === 'es' ? 'Importar' : 'Import'}</Button>
                      </div>
                    ))}</div>
                  )}
                </div>
              )}
              {showAddQ && (
                <div className="border border-blue-200 bg-blue-50/30 rounded-sm p-3 mb-3">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-xs font-semibold text-stone-700">{lb.select_questions}</p>
                    <Select value={filterCat} onValueChange={setFilterCat}>
                      <SelectTrigger className="w-32 h-7 text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="all">{lb.all}</SelectItem>{Object.entries(catLabels).map(([k, v]) => (<SelectItem key={k} value={k}>{v}</SelectItem>))}</SelectContent>
                    </Select>
                  </div>
                  <div className="max-h-[200px] overflow-y-auto space-y-1">
                    {filteredBank.length === 0 ? (<p className="text-xs text-stone-400 text-center py-2">{lang === 'es' ? 'No hay preguntas disponibles' : 'No questions available'}</p>) : filteredBank.map(bq => (
                      <div key={bq.question_id} className="flex items-center justify-between bg-white border border-stone-200 rounded-sm px-3 py-1.5">
                        <div className="flex items-center gap-2"><span className="text-xs text-stone-800">{bq.text_es}</span><Badge variant="outline" className={`text-[10px] ${catColors[bq.category] || ''}`}>{catLabels[bq.category] || bq.category}</Badge></div>
                        {selectedIds.has(bq.question_id) ? (<Check size={14} className="text-green-500" />) : (<Button variant="ghost" size="icon" className="h-6 w-6" data-testid={`add-bank-q-${bq.question_id}`} onClick={() => addBankQuestion(bq)}><Plus size={12} /></Button>)}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {showCustomQ && (
                <div className="border border-violet-200 bg-violet-50/30 rounded-sm p-3 mb-3">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-xs font-semibold text-stone-700">
                      {editingQuestionIdx !== null
                        ? (lang === 'es' ? `Editar pregunta específica (#${editingQuestionIdx + 1})` : `Edit custom question (#${editingQuestionIdx + 1})`)
                        : lb.custom_question}
                    </p>
                    {editingQuestionIdx !== null && (
                      <Button
                        variant="ghost"
                        size="sm"
                        type="button"
                        className="text-[11px] h-6 px-2 text-stone-500"
                        data-testid="cancel-edit-custom-question"
                        onClick={cancelCustomQuestion}
                      >
                        <X size={12} className="mr-1" />{lang === 'es' ? 'Cancelar' : 'Cancel'}
                      </Button>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Input data-testid="custom-question-text" value={customQ.text_es} onChange={e => setCustomQ(p => ({ ...p, text_es: e.target.value }))} placeholder={lang === 'es' ? 'Texto de la pregunta (ES)' : 'Question text (ES)'} className="text-sm" />
                    <Input data-testid="custom-question-text-en" value={customQ.text_en} onChange={e => setCustomQ(p => ({ ...p, text_en: e.target.value }))} placeholder={lang === 'es' ? 'Texto de la pregunta (EN)' : 'Question text (EN)'} className="text-sm" />
                    <div className="flex gap-2">
                      <Select value={customQ.type} onValueChange={v => setCustomQ(p => ({ ...p, type: v }))}>
                        <SelectTrigger data-testid="custom-question-type-select" className="w-44 text-xs"><SelectValue /></SelectTrigger>
                        <SelectContent>{[
                          { v: 'text',             es: 'Texto',               en: 'Text' },
                          { v: 'textarea',         es: 'Área de texto',       en: 'Textarea' },
                          { v: 'number',           es: 'Número',              en: 'Number' },
                          { v: 'date',             es: 'Fecha',               en: 'Date' },
                          { v: 'scale',            es: 'Escala (estrellas)',  en: 'Scale (stars)' },
                          { v: 'select',           es: 'Selector',            en: 'Select' },
                          { v: 'multiselect',      es: 'Multiselección',      en: 'Multi-select' },
                          { v: 'checklist',        es: 'Lista de verificación (checkboxes)', en: 'Checklist (checkboxes)' },
                          { v: 'radio',            es: 'Opción única',        en: 'Single choice' },
                          { v: 'boolean',          es: 'Sí / No',             en: 'Yes / No' },
                          { v: 'boolean_with_obs', es: 'Sí / No + Obs.',      en: 'Yes / No + Obs.' },
                          { v: 'nps',              es: 'NPS (0-10)',          en: 'NPS (0-10)' },
                          { v: 'nps_multi',        es: 'NPS múltiple (varios sub-apartados)', en: 'Multi NPS (several sub-items)' },
                          { v: 'likert_1_5',       es: 'Satisfacción (Likert 1-5)', en: 'Satisfaction (Likert 1-5)' },
                          { v: 'likert_1_7',       es: 'Satisfacción (Likert 1-7)', en: 'Satisfaction (Likert 1-7)' },
                          { v: 'masi',             es: 'MASI (Melasma)',      en: 'MASI (Melasma)' },
                          { v: 'table_text',       es: 'Tabla (opciones + texto)',     en: 'Table (options + text)' },
                          { v: 'table_select',     es: 'Tabla (opciones + selector)',  en: 'Table (options + select)' },
                          { v: 'custom_table',     es: 'Tabla personalizada (NxM)',    en: 'Custom table (NxM)' },
                          { v: 'composite_scale',  es: 'Escala compuesta (sub-escalas con suma)', en: 'Composite scale (sub-scales with sum)' },
                        ].map(opt => (<SelectItem key={opt.v} value={opt.v}>{lang === 'es' ? opt.es : opt.en}</SelectItem>))}</SelectContent>
                      </Select>
                      {/* URS-F296: selector de categoria. Define si la pregunta puede aparecer como Evento Adverso. */}
                      <Select value={customQ.category || 'general'} onValueChange={v => setCustomQ(p => ({ ...p, category: v }))}>
                        <SelectTrigger data-testid="custom-question-category-select" className="w-40 text-xs">
                          <SelectValue placeholder={lang === 'es' ? 'Categoría' : 'Category'} />
                        </SelectTrigger>
                        <SelectContent>{[
                          { v: 'general',     es: 'General',     en: 'General' },
                          { v: 'eficacia',    es: 'Eficacia',    en: 'Efficacy' },
                          { v: 'seguridad',   es: 'Seguridad (EA si Sí)',  en: 'Safety (AE if Yes)' },
                          { v: 'tolerancia',  es: 'Tolerancia (EA si >0)', en: 'Tolerance (AE if >0)' },
                          { v: 'dermatologia', es: 'Dermatología', en: 'Dermatology' },
                        ].map(opt => (<SelectItem key={opt.v} value={opt.v}>{lang === 'es' ? opt.es : opt.en}</SelectItem>))}</SelectContent>
                      </Select>
                      {['scale', 'select', 'multiselect', 'radio', 'nps_multi', 'checklist'].includes(customQ.type) && (<Input value={customOpts} onChange={e => setCustomOpts(e.target.value)} placeholder={customQ.type === 'nps_multi' ? (lang === 'es' ? 'Sub-apartados (coma): Recomendarías, Volverías a comprar...' : 'Sub-items (comma): Would recommend, Would buy again...') : customQ.type === 'checklist' ? (lang === 'es' ? 'Items de verificación (coma): Alergias, Embarazo, Lactancia...' : 'Checklist items (comma): Allergies, Pregnancy, Lactation...') : (lang === 'es' ? 'Opciones (coma)' : 'Options (comma)')} className="text-xs flex-1" data-testid={customQ.type === 'nps_multi' ? 'custom-question-nps-multi-options' : customQ.type === 'checklist' ? 'custom-question-checklist-options' : 'custom-question-options'} />)}
                      {/* URS-F297: campos extra para tipos tabla. `customOpts` se reutiliza como FILAS. */}
                      {(customQ.type === 'table_text' || customQ.type === 'table_select') && (
                        <Input
                          value={customOpts}
                          onChange={e => setCustomOpts(e.target.value)}
                          placeholder={lang === 'es' ? 'Filas: Producto 1, Producto 2, ##Sección, Producto 3' : 'Rows: Item 1, Item 2, ##Section, Item 3'}
                          className="text-xs flex-1"
                          data-testid="custom-table-rows-input"
                        />
                      )}
                      {customQ.type === 'table_select' && (
                        <Input
                          value={(customQ.cell_options || []).join(', ')}
                          onChange={e => setCustomQ(p => ({ ...p, cell_options: e.target.value.split(',').map(s => s.trim()).filter(Boolean) }))}
                          placeholder={lang === 'es' ? 'Opciones del selector: 0, 1, 2, 3' : 'Cell options: 0, 1, 2, 3'}
                          className="text-xs flex-1"
                          data-testid="custom-table-cell-options-input"
                        />
                      )}
                      {customQ.type === 'custom_table' && (
                        <CustomTableDesigner
                          value={customQ}
                          onChange={updater => setCustomQ(p => ({ ...p, ...(typeof updater === 'function' ? updater(p) : updater) }))}
                          lang={lang}
                        />
                      )}
                      {customQ.type === 'composite_scale' && (
                        <div className="w-full bg-violet-50/40 border border-violet-200 rounded-sm p-2">
                          <CompositeScaleDesigner
                            value={customQ}
                            onChange={patch => setCustomQ(p => ({ ...p, ...patch }))}
                            lang={lang}
                          />
                        </div>
                      )}
                      {(customQ.type === 'likert_1_5' || customQ.type === 'likert_1_7') && (
                        <span className="text-[10px] text-stone-500 self-center flex-1 italic">
                          {customQ.type === 'likert_1_5'
                            ? (lang === 'es' ? 'Escala fija 1 (Totalmente en desacuerdo) → 5 (Totalmente de acuerdo).' : 'Fixed 1 (Strongly disagree) → 5 (Strongly agree).')
                            : (lang === 'es' ? 'Escala fija 1 (Me disgusta mucho) → 7 (Me gusta mucho).' : 'Fixed 1 (Strongly dislike) → 7 (Strongly like).')}
                        </span>
                      )}
                      <Button size="sm" data-testid="confirm-custom-question-btn" onClick={addCustomQuestion} className="bg-[#1f3e80] hover:bg-[#2a4f8a] text-white text-xs rounded-sm" title={editingQuestionIdx !== null ? (lang === 'es' ? 'Guardar cambios' : 'Save changes') : (lang === 'es' ? 'Añadir pregunta' : 'Add question')}>
                        {editingQuestionIdx !== null ? <Check size={12} /> : <Plus size={12} />}
                      </Button>
                    </div>
                    {/* Logica condicional: mostrar esta pregunta solo si una Si/No anterior cumple. */}
                    {(() => {
                      const candidates = form.questions.filter((qq, i) =>
                        (qq.type === 'boolean' || qq.type === 'boolean_with_obs') &&
                        qq.question_id &&
                        (editingQuestionIdx === null || i !== editingQuestionIdx)
                      );
                      if (candidates.length === 0) return null;
                      return (
                        <div className="flex items-center gap-2 flex-wrap bg-amber-50/50 border border-amber-200 rounded-sm px-2 py-1.5" data-testid="custom-question-condition">
                          <span className="text-[11px] font-medium text-amber-800">{lang === 'es' ? 'Mostrar solo si' : 'Show only if'}</span>
                          <Select value={customQ.condition_qid || '__none'} onValueChange={v => setCustomQ(p => ({ ...p, condition_qid: v }))}>
                            <SelectTrigger data-testid="custom-question-condition-q" className="w-56 text-xs h-7"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="__none">{lang === 'es' ? 'Siempre visible' : 'Always visible'}</SelectItem>
                              {candidates.map(qq => (
                                <SelectItem key={qq.question_id} value={qq.question_id}>{(qq.text_es || qq.text_en || qq.question_id).slice(0, 60)}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          {customQ.condition_qid && customQ.condition_qid !== '__none' && (
                            <Select value={customQ.condition_equals || 'si'} onValueChange={v => setCustomQ(p => ({ ...p, condition_equals: v }))}>
                              <SelectTrigger data-testid="custom-question-condition-val" className="w-28 text-xs h-7"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="si">= {lang === 'es' ? 'Sí' : 'Yes'}</SelectItem>
                                <SelectItem value="no">= No</SelectItem>
                              </SelectContent>
                            </Select>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                </div>
              )}
            </div>
          </div>
          <DialogFooter className="border-t border-stone-200 pt-4">
            <Button variant="ghost" onClick={() => setShowDialog(false)}>{lb.cancel}</Button>
            <Button data-testid="save-questionnaire-btn" onClick={handleSave} disabled={saving} className="bg-[#1f3e80] hover:bg-[#2a4f8a] text-white">{saving ? '...' : lb.save}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <ConfirmDeleteDialog
        open={!!confirmDeleteSQ}
        onOpenChange={(v) => { if (!v) setConfirmDeleteSQ(null); }}
        title={lang === 'es' ? 'Eliminar cuestionario' : 'Delete questionnaire'}
        description={lang === 'es' ? `¿Estás seguro de que deseas eliminar el cuestionario "${confirmDeleteSQ?.name || ''}"?` : `Are you sure you want to delete questionnaire "${confirmDeleteSQ?.name || ''}"?`}
        onConfirm={() => handleDelete(confirmDeleteSQ?.questionnaire_id)}
      />
      <QuestionnairePreviewDialog
        open={!!previewQ}
        onClose={() => setPreviewQ(null)}
        questionnaire={previewQ}
        lang={lang}
      />
    </div>
  );
}

export function SamplesPanel({ studyId, lang }) {
  const navigate = useNavigate();
  const [samples, setSamples] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDispense, setShowDispense] = useState(null);
  const [dispForm, setDispForm] = useState({ volunteer_id: '', visit_code: '', quantity: 1, date: '', notes: '' });
  const [studyVols, setStudyVols] = useState([]);
  const [studyVisits, setStudyVisits] = useState([]);
  const [saving, setSaving] = useState(false);
  // URS-F303: detalle de muestra (mismo popup que en /warehouse).
  const [detail, setDetail] = useState(null);
  const [movements, setMovements] = useState([]);
  const [dispensations, setDispensations] = useState([]);

  const lb = lang === 'es'
    ? { title: 'Muestras del estudio', no_samples: 'No hay muestras asociadas a este estudio', id: 'ID', product: 'Producto', lot: 'Lote', stock: 'Stock', expiry: 'Caducidad', storage: 'Almacenamiento', status: 'Estado', go_warehouse: 'Ir al Almacen', add_sample: 'Nueva muestra', recibido: 'Recibido', en_uso: 'En uso', agotado: 'Agotado', devuelto: 'Devuelto', destruido: 'Destruido', blind: 'Ciego', location: 'Ubicacion', dispense: 'Dispensar', volunteer: 'Voluntario', visit: 'Visita', quantity: 'Cantidad', date: 'Fecha', notes: 'Notas', cancel: 'Cancelar', remaining: 'Restante', actions: 'Acciones', detail: 'Detalle de muestra', history: 'Historial', qty_received: 'Cantidad recibida', unit_weight: 'Peso por unidad (g)', blind_code: 'Codigo ciego', view: 'Ver detalle' }
    : { title: 'Study samples', no_samples: 'No samples associated with this study', id: 'ID', product: 'Product', lot: 'Lot', stock: 'Stock', expiry: 'Expiry', storage: 'Storage', status: 'Status', go_warehouse: 'Go to Warehouse', add_sample: 'New sample', recibido: 'Received', en_uso: 'In use', agotado: 'Depleted', devuelto: 'Returned', destruido: 'Destroyed', blind: 'Blind', location: 'Location', dispense: 'Dispense', volunteer: 'Volunteer', visit: 'Visit', quantity: 'Quantity', date: 'Date', notes: 'Notes', cancel: 'Cancel', remaining: 'Remaining', actions: 'Actions', detail: 'Sample detail', history: 'History', qty_received: 'Qty received', unit_weight: 'Unit weight (g)', blind_code: 'Blind code', view: 'View detail' };

  const load = () => {
    if (!studyId) return;
    axios.get(`${API_URL}/api/samples?study_id=${studyId}`, { withCredentials: true })
      .then(r => setSamples(r.data)).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, [studyId]);

  const openDispense = async (s) => {
    setShowDispense(s);
    setDispForm({ volunteer_id: '', visit_code: '', quantity: 1, date: new Date().toISOString().split('T')[0], notes: '' });
    try {
      const [vr, visr] = await Promise.all([
        axios.get(`${API_URL}/api/studies/${studyId}/volunteers`, { withCredentials: true }),
        axios.get(`${API_URL}/api/studies/${studyId}/visits`, { withCredentials: true }),
      ]);
      // URS-F362: incluir tambien voluntarios 'completado' (ya terminada su
      // ultima visita) para que no desaparezcan de ninguna parte del estudio.
      setStudyVols(vr.data.filter(v => v.status === 'incluido' || v.status === 'completado'));
      setStudyVisits(visr.data);
    } catch { }
  };

  // URS-F303: mismo popup de detalle que en /warehouse (movements + dispensations).
  const openDetail = async (s) => {
    setDetail(s);
    try {
      const [mr, dr] = await Promise.all([
        axios.get(`${API_URL}/api/samples/${s.sample_id}/movements`, { withCredentials: true }),
        axios.get(`${API_URL}/api/samples/${s.sample_id}/dispensations`, { withCredentials: true }),
      ]);
      setMovements(mr.data);
      setDispensations(dr.data);
    } catch {
      setMovements([]); setDispensations([]);
    }
  };

  const handleDispense = async () => {
    if (!dispForm.volunteer_id) { toast.error(lang === 'es' ? 'Selecciona un voluntario' : 'Select a volunteer'); return; }
    setSaving(true);
    try {
      await axios.post(`${API_URL}/api/dispensations`, { sample_id: showDispense.sample_id, ...dispForm }, { withCredentials: true });
      toast.success(lang === 'es' ? 'Muestra dispensada correctamente' : 'Sample dispensed successfully');
      setShowDispense(null); load();
    } catch (err) { toast.error(errText(err)); }
    setSaving(false);
  };

  if (!studyId) return <p className="text-sm text-stone-500 text-center py-8">{lang === 'es' ? 'Guarda el estudio primero' : 'Save the study first'}</p>;

  return (
    <div className="space-y-4">
      <div className="flex justify-end gap-2">
        <Button variant="outline" data-testid="go-warehouse-btn" onClick={() => navigate('/warehouse')} className="rounded-sm gap-2 text-sm"><Package size={14} />{lb.go_warehouse}</Button>
      </div>
      {loading ? (<div className="p-12 text-center text-sm text-stone-500">{lang === 'es' ? 'Cargando...' : 'Loading...'}</div>
      ) : samples.length === 0 ? (
        <div className="bg-white border border-stone-200 rounded-sm p-12 text-center"><Package size={40} className="text-stone-300 mx-auto mb-3" /><p className="text-sm text-stone-500">{lb.no_samples}</p></div>
      ) : (
        <div className="bg-white border border-stone-200 rounded-sm overflow-hidden">
          <Table>
            <TableHeader><TableRow>
              <TableHead className="w-28">{lb.id}</TableHead><TableHead>{lb.product}</TableHead><TableHead className="w-28">{lb.lot}</TableHead><TableHead className="w-28">{lb.stock}</TableHead><TableHead className="w-28">{lb.expiry}</TableHead><TableHead className="w-28">{lb.storage}</TableHead><TableHead className="w-28">{lb.location}</TableHead><TableHead className="w-24">{lb.status}</TableHead><TableHead className="w-24">{lb.actions}</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {samples.map(s => {
                const isExpiring = s.expiry_date && new Date(s.expiry_date) < new Date(Date.now() + 30 * 86400000);
                return (
                  <TableRow key={s.sample_id} data-testid={`study-sample-${s.sample_id}`} className="hover:bg-stone-50">
                    <TableCell className="font-mono text-xs text-stone-600">{s.sample_id}{s.is_blind && <Badge variant="outline" className="text-[9px] ml-1 bg-violet-50 text-violet-600 border-violet-200">{lb.blind}</Badge>}</TableCell>
                    <TableCell className="text-sm font-medium text-stone-900">{s.product_name}</TableCell>
                    <TableCell className="text-xs font-mono text-stone-600">{s.manufacturer_lot || '-'}</TableCell>
                    <TableCell className="text-sm"><span className="font-semibold text-stone-900">{s.stock_remaining}</span><span className="text-xs text-stone-400">/{s.quantity_received}</span></TableCell>
                    <TableCell className={`text-xs ${isExpiring ? 'text-red-600 font-semibold' : 'text-stone-600'}`}>{s.expiry_date || '-'}</TableCell>
                    <TableCell className="text-xs text-stone-600">{storageLabelsStudy[lang]?.[s.storage_condition] || s.storage_condition}</TableCell>
                    <TableCell className="text-xs text-stone-600">{s.location || '-'}</TableCell>
                    <TableCell><Badge variant="outline" className={`text-xs ${sampleStatusColors[s.status] || ''}`}>{lb[s.status] || s.status}</Badge></TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Button variant="ghost" size="icon" className="h-7 w-7" data-testid={`study-view-sample-${s.sample_id}`} onClick={() => openDetail(s)} title={lb.view}><Eye size={14} /></Button>
                        <Button variant="ghost" size="sm" className="h-7 text-xs gap-1 text-green-700 hover:text-green-800 hover:bg-green-50" data-testid={`study-dispense-${s.sample_id}`} onClick={() => openDispense(s)} disabled={s.stock_remaining <= 0}><Plus size={12} />{lb.dispense}</Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}
      <Dialog open={!!showDispense} onOpenChange={() => setShowDispense(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{lb.dispense}: {showDispense?.product_name}</DialogTitle>
            <DialogDescription className="font-mono text-xs">{showDispense?.sample_id} — {lb.remaining}: {showDispense?.stock_remaining}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5"><Label>{lb.volunteer} <span className="text-red-500">*</span></Label>
              <Select value={dispForm.volunteer_id || '_n'} onValueChange={v => setDispForm(p => ({ ...p, volunteer_id: v === '_n' ? '' : v }))}>
                <SelectTrigger data-testid="study-dispense-volunteer-select"><SelectValue /></SelectTrigger>
                <SelectContent>{studyVols.map(v => (<SelectItem key={v.volunteer_id} value={v.volunteer_id}>{v.study_volunteer_code} — {v.volunteer_name}</SelectItem>))}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label>{lb.visit}</Label>
              <Select value={dispForm.visit_code || '_n'} onValueChange={v => setDispForm(p => ({ ...p, visit_code: v === '_n' ? '' : v }))}>
                <SelectTrigger data-testid="study-dispense-visit-select"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="_n">-</SelectItem>{studyVisits.map(v => (<SelectItem key={v.visit_id} value={v.code}>{v.code} — {v.name}</SelectItem>))}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5"><Label>{lb.quantity}</Label><Input data-testid="study-dispense-qty" type="number" min="1" max={showDispense?.stock_remaining || 1} value={dispForm.quantity} onChange={e => setDispForm(p => ({ ...p, quantity: parseInt(e.target.value) || 1 }))} /></div>
              <div className="space-y-1.5"><Label>{lb.date}</Label><Input data-testid="study-dispense-date" type="date" value={dispForm.date} onChange={e => setDispForm(p => ({ ...p, date: e.target.value }))} /></div>
            </div>
            <div className="space-y-1.5"><Label>{lb.notes}</Label><Textarea data-testid="study-dispense-notes" value={dispForm.notes} onChange={e => setDispForm(p => ({ ...p, notes: e.target.value }))} placeholder={lang === 'es' ? 'Notas opcionales...' : 'Optional notes...'} className="min-h-[40px]" /></div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowDispense(null)}>{lb.cancel}</Button>
            <Button data-testid="study-confirm-dispense-btn" onClick={handleDispense} disabled={saving} className="bg-[#1f3e80] hover:bg-[#2a4f8a] text-white rounded-sm gap-1"><Plus size={14} />{saving ? '...' : lb.dispense}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* URS-F303: detalle de muestra (mismo popup que /warehouse) */}
      <SampleDetailDialog
        sample={detail}
        movements={movements}
        dispensations={dispensations}
        lang={lang}
        onClose={() => setDetail(null)}
      />
    </div>
  );
}

export function StaffPanel({ staff, onChange, lang, studyId, isDentalStudy }) {
  const [allUsers, setAllUsers] = useState([]);
  const [staffNames, setStaffNames] = useState({}); // URS-F327: id -> {name, specialty}
  const [loading, setLoading] = useState(true);
  const [openRole, setOpenRole] = useState(null);

  useEffect(() => {
    // URS-F327 + URS-F360: dos fuentes de datos.
    //  1) /api/users (solo admin) o /api/users/staff-pool (admin + roles de
    //     gestion como director_estudio) → lista completa para popular el
    //     dropdown de "Asignar". Si el usuario no es admin, /api/users falla
    //     con 403 y caemos al pool reducido.
    //  2) /api/studies/{id}/staff-names → mapping {id, name, specialty} solo
    //     de los UIDs ya en staff (chips con nombres).
    const users$ = axios.get(`${API_URL}/api/users`, { withCredentials: true })
      .then(r => setAllUsers(r.data))
      .catch(() => axios.get(`${API_URL}/api/users/staff-pool`, { withCredentials: true })
        .then(r => setAllUsers(r.data))
        .catch(() => {}));
    const names$ = studyId
      ? axios.get(`${API_URL}/api/studies/${studyId}/staff-names`, { withCredentials: true })
          .then(r => {
            const map = {};
            (r.data || []).forEach(u => { map[u.id] = u; });
            setStaffNames(map);
          })
          .catch(() => {})
      : Promise.resolve();
    Promise.all([users$, names$]).finally(() => setLoading(false));
  }, [studyId]);

  const toggleUser = (roleKey, userId) => {
    const current = staff[roleKey] || [];
    if (current.includes(userId)) {
      onChange({ ...staff, [roleKey]: current.filter(u => u !== userId) });
    } else {
      onChange({ ...staff, [roleKey]: [...current, userId] });
    }
  };
  const removeUser = (roleKey, userId) => { onChange({ ...staff, [roleKey]: (staff[roleKey] || []).filter(u => u !== userId) }); };
  // URS-F327: resolver primero via /api/users (admin), luego via staff-names.
  const getUserById = (userId) => {
    const u = allUsers.find(x => x.id === userId);
    if (u) return u;
    const sn = staffNames[userId];
    if (sn) return { id: userId, name: sn.name, specialty: sn.specialty };
    return null;
  };

  if (loading) return <div className="text-center py-8 text-stone-400">{lang === 'es' ? 'Cargando...' : 'Loading...'}</div>;

  return (
    <div className="space-y-3" data-testid="staff-panel">
      <p className="text-xs text-stone-500 mb-2">{lang === 'es' ? 'Asigna el personal responsable de este estudio por cada rol.' : 'Assign the staff responsible for this study by role.'}</p>
      {(() => {
        // URS-F431: en estudios dentales (Cosmetico/Dental,
        // Cosmetico Plus/Dental o Producto Sanitario/Odontologia) se anade
        // el rol "Aux Odontologia" que solo admite usuarios con rol global
        // "odontologia".
        const rolesForStudy = isDentalStudy
          ? [...STAFF_ROLES, { key: 'aux_odontologia', label_es: 'Aux Odontologia', label_en: 'Dental Assistant' }]
          : STAFF_ROLES;
        return rolesForStudy;
      })().map(role => {
        const assigned = staff[role.key] || [];
        const ipAssigned = staff['investigador_principal'] || [];
        const monitorAssigned = staff['monitor'] || [];
        const directorEstudioAssigned = staff['director_estudio'] || [];
        // URS-F193 / URS-F336: Sondas y Reclutamiento (operativos) admiten
        // a sus supervisores. Los slots "Responsable de Sondas" y
        // "Responsable de Reclutamiento" se reservan a los supervisores.
        const ROLE_INCLUDES = {
          sondas: ['sondas', 'supervisor_sondas'],
          reclutamiento: ['reclutamiento', 'supervisor_reclutamiento'],
          responsable_sondas: ['supervisor_sondas'],
          responsable_reclutamiento: ['supervisor_reclutamiento'],
          // URS-F431: "Aux Odontologia" acepta a usuarios con rol global
          // "odontologia".
          aux_odontologia: ['odontologia'],
        };
        const acceptedRoles = ROLE_INCLUDES[role.key] || [role.key];
        let eligible = allUsers.filter(u => {
          const userRoles = u.roles || [];
          return acceptedRoles.some(r => userRoles.includes(r))
            || (role.key === 'director_estudio' && userRoles.includes('admin'));
        });
        // Monitor cannot be IP or Director Estudio
        if (role.key === 'monitor') {
          eligible = eligible.filter(u => !ipAssigned.includes(u.id) && !directorEstudioAssigned.includes(u.id));
        }
        // IP and Director Estudio cannot be Monitor
        if (role.key === 'investigador_principal' || role.key === 'director_estudio') {
          eligible = eligible.filter(u => !monitorAssigned.includes(u.id));
        }
        const isOpen = openRole === role.key;
        return (
          <div key={role.key} className="bg-white border border-stone-200 rounded-sm p-4" data-testid={`staff-role-${role.key}`}>
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <UsersThree size={16} className="text-emerald-700" />
                <span className="text-sm font-semibold text-stone-800">{lang === 'es' ? role.label_es : role.label_en}</span>
                {assigned.length > 0 && (<Badge variant="outline" className="text-[10px]">{assigned.length}</Badge>)}
              </div>
              <Button variant="outline" size="sm" className="text-xs gap-1" onClick={() => setOpenRole(isOpen ? null : role.key)} data-testid={`staff-add-${role.key}`}><Plus size={12} /> {lang === 'es' ? 'Asignar' : 'Assign'}</Button>
            </div>
            {assigned.length > 0 ? (
              <div className="flex flex-wrap gap-2 ml-6">{assigned.map(uid => {
                const user = getUserById(uid);
                return (<span key={uid} className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-emerald-50 border border-emerald-200 rounded-sm text-xs text-emerald-800">
                  {user ? user.name : uid}{user?.specialty && <span className="text-emerald-500">({user.specialty})</span>}
                  <button type="button" onClick={() => removeUser(role.key, uid)} className="text-emerald-400 hover:text-red-500 ml-0.5" data-testid={`staff-remove-${role.key}-${uid}`}><X size={12} /></button>
                </span>);
              })}</div>
            ) : (<p className="text-xs text-stone-400 ml-6">{lang === 'es' ? 'Sin personal asignado' : 'No staff assigned'}</p>)}
            {isOpen && (
              <div className="mt-3 ml-6 border border-stone-200 rounded-sm bg-stone-50 p-2 max-h-48 overflow-y-auto">
                {eligible.length > 0 ? eligible.map(u => {
                  const isSelected = assigned.includes(u.id);
                  return (<button key={u.id} type="button" className={`w-full text-left px-3 py-2 rounded-sm text-sm flex items-center justify-between ${isSelected ? 'bg-emerald-50 border border-emerald-200' : 'hover:bg-white border border-transparent'}`} onClick={() => toggleUser(role.key, u.id)} data-testid={`staff-select-${role.key}-${u.id}`}>
                    <span><span className="font-medium text-stone-800">{u.name}</span><span className="text-stone-400 text-xs ml-2">{u.email}</span>{u.specialty && <span className="text-stone-400 text-xs ml-1">· {u.specialty}</span>}</span>
                    {isSelected && <Check size={16} className="text-emerald-600" />}
                  </button>);
                }) : (<p className="text-xs text-stone-400 text-center py-2">{lang === 'es' ? `No hay usuarios con rol "${role.label_es}"` : `No users with role "${role.label_en}"`}</p>)}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// --- Satisfaction questionnaire importer (URS-F211) ---
function SatisfactionImportButton({ studyId, lang, onImported }) {
  const [open, setOpen] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(false);

  const loadTemplates = () => {
    axios.get(`${API_URL}/api/config/satisfaction-questionnaires`, { withCredentials: true })
      .then(r => setTemplates(r.data || []))
      .catch(() => {});
  };

  const handleOpen = () => { setOpen(true); loadTemplates(); setSelected(null); };

  const handleImport = async () => {
    if (!selected) { toast.error(lang === 'es' ? 'Selecciona un cuestionario' : 'Select one'); return; }
    setLoading(true);
    try {
      await axios.post(
        `${API_URL}/api/studies/${studyId}/satisfaction-questionnaires/import`,
        { config_questionnaire_id: selected },
        { withCredentials: true },
      );
      toast.success(lang === 'es' ? 'Cuestionario importado' : 'Imported');
      setOpen(false); setSelected(null);
      if (onImported) onImported();
    } catch (e) { toast.error(errText(e, 'Error')); }
    setLoading(false);
  };

  return (
    <>
      <Button
        data-testid="import-satisfaction-q-btn"
        variant="outline"
        onClick={handleOpen}
        className="rounded-sm gap-2 border-violet-300 text-violet-700 hover:bg-violet-50"
      >
        <Plus size={16} />{lang === 'es' ? 'Importar de Satisfacción' : 'Import from Satisfaction'}
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>{lang === 'es' ? 'Importar cuestionario de satisfacción' : 'Import satisfaction questionnaire'}</DialogTitle>
            <DialogDescription className="text-xs">
              {lang === 'es'
                ? 'Selecciona una plantilla creada en Configuración → Cuestionarios → Satisfacción. Se clonará en este estudio y podrás asignarla a cualquier visita (presencial o por email).'
                : 'Select a template from Settings → Questionnaires → Satisfaction. It will be cloned into this study.'}
            </DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto space-y-2 pr-1">
            {templates.length === 0 ? (
              <p className="text-sm text-stone-500 text-center py-8">
                {lang === 'es' ? 'No hay plantillas. Crea una primero en Configuración → Cuestionarios → Satisfacción.' : 'No templates. Create one in Settings first.'}
              </p>
            ) : templates.map(t => {
              const m = t.satisfaction_meta || {};
              const isSel = selected === t.questionnaire_id;
              return (
                <button
                  key={t.questionnaire_id}
                  type="button"
                  data-testid={`sat-import-option-${t.questionnaire_id}`}
                  onClick={() => setSelected(t.questionnaire_id)}
                  className={`w-full text-left p-3 rounded-sm border transition ${isSel ? 'bg-violet-50 border-violet-300' : 'bg-white border-stone-200 hover:border-violet-200'}`}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-sm font-semibold text-stone-900">{t.name}</span>
                    <Badge variant="outline" className="text-[10px] bg-violet-50 text-violet-700 border-violet-200">Likert 1-5</Badge>
                    <Badge variant="outline" className="text-[10px] font-mono">{(t.questions || []).length}</Badge>
                  </div>
                  <div className="text-[11px] text-stone-600 flex flex-wrap gap-1.5">
                    {m.area_name && <span><b>Área:</b> {m.area_name}</span>}
                    {m.family_name && <span>· {m.family_name}</span>}
                    {m.periodo_uso && <span>· {m.periodo_uso}</span>}
                  </div>
                </button>
              );
            })}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>{lang === 'es' ? 'Cancelar' : 'Cancel'}</Button>
            <Button data-testid="confirm-import-sat-q-btn" onClick={handleImport} disabled={!selected || loading} className="bg-[#1f3e80] hover:bg-[#2a4f8a] text-white">
              {loading ? '...' : (lang === 'es' ? 'Importar' : 'Import')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
